/**
 * useMultiAgentMetadataItem — native content loader for the multi-agent editor.
 *
 * Replaces the dev-agent `useMetadataItem` (which goes through the Elixir
 * GraphQL metadata service — unavailable locally and not native to multi-agent,
 * producing "Metadata item not found" on file open). This hits the multi-agent
 * backend's own endpoint via `getMetadataItem` (`/api/chat/metadata/items/{id}`
 * for canonical ids, the artifact endpoint for `art_…`), org/user-scoped, and
 * shapes the result into the same `MetadataItem` the editor already consumes.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getFormatApiLanguage } from "@ncoder/ui";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { getMetadataItem } from "@/pages/multi-agent/ide/panels/resolveDependencyItemForEditor";
import { formatCode } from "@/hooks/metadata_v1/useFormatCode";
import type { MetadataItem } from "@/services/metadata_v1/types";

/** Best-effort prettify on open. Silent on any failure (e.g. format service
 *  unavailable) — returns the raw source so the file still loads. Formatting
 *  the loaded content (not a later edit) means it opens pretty AND clean: the
 *  formatted text becomes the editor's baseline, so it isn't marked dirty. */
async function formatOnOpen(
  content: string,
  metadataType: string,
  fileName: string,
  orgId?: string | null,
): Promise<string> {
  if (!content.trim()) return content;
  try {
    const lang = getFormatApiLanguage(metadataType, fileName);
    if (!lang) return content;
    const res = await formatCode({ code: content, language: lang }, orgId);
    if (res && !res.error && res.formatted) return res.formatted;
  } catch {
    /* keep raw content */
  }
  return content;
}

export function useMultiAgentMetadataItem(
  id: string | null | undefined,
  options?: { enabled?: boolean },
): UseQueryResult<MetadataItem, Error> {
  const { enabled = true } = options || {};
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id);

  return useQuery<MetadataItem, Error>({
    queryKey: ["multiAgentMetadataItem", orgId, id],
    queryFn: async () => {
      const item = await getMetadataItem(id!, orgId, true);
      const metadataType = item.metadata_type || "ApexClass";
      const fileName = item.name || item.full_name || item.id;
      const content = await formatOnOpen(item.content ?? "", metadataType, fileName, orgId);
      return {
        __typename: "MetadataItem",
        id: item.id,
        fileName,
        metadataType,
        content,
      } as MetadataItem;
    },
    // `art_…` ids resolve through the artifact endpoint (no org needed); a
    // canonical metadata id needs the selected org for scoping.
    enabled: enabled && !!id && (id!.startsWith("art_") || !!orgId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}
